import React, { useState, useEffect, useCallback, useRef } from 'react';
import './AnimatedTextDisplay.css';

const AnimatedTextDisplay = ({ isActive, onVideoShow }) => {
  const [showText, setShowText] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [isButton1Pressed, setIsButton1Pressed] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (isActive) {
      // Small delay before starting animation
      const timer = setTimeout(() => {
        setShowText(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowText(false);
      setShowVideo(false);
      setIsButton1Pressed(false);
      if (onVideoShow) onVideoShow(false);
      // Pause video if it's playing
      if (videoRef.current) {
        videoRef.current.pause();
      }
    }
  }, [isActive, onVideoShow]);

  // Notify parent when video shows
  useEffect(() => {
    if (onVideoShow) {
      onVideoShow(showVideo);
    }
  }, [showVideo, onVideoShow]);

  const handleButton1Click = useCallback(() => {
    console.log('Opening: How this study\'s drug can rescue function in neurons');
    
    // Show button pressed state
    setIsButton1Pressed(true);
    
    // Wait 1 second, then show video
    setTimeout(() => {
      setShowVideo(true);
      setIsButton1Pressed(false);
    }, 1000);
  }, []);

  // Keyboard listener for right arrow
  useEffect(() => {
    if (!isActive || showVideo) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleButton1Click();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, showVideo, handleButton1Click]);

  const handleButton2Click = () => {
    console.log('Opening: The truth about vitamin supplements for dementia');
    // Could open a URL: window.open('https://example.com/paper2', '_blank');
  };

  return (
    <div className="animated-text-container">
      {!showVideo && (
        <>
          {/* Background particles */}
          <div className="particles-background">
            {[...Array(30)].map((_, i) => (
              <div 
                key={i} 
                className="particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  animationDuration: `${3 + Math.random() * 2}s`
                }}
              />
            ))}
          </div>

          {/* Gradient orb */}
          <div className="gradient-orb"></div>
          <div className="gradient-orb-2"></div>

          {/* Main text content */}
          <div className={`text-content ${showText ? 'visible' : ''}`}>
            <div className="text-line line-1">
              <span className="word word-1">The</span>
              <span className="word word-2">latest</span>
              <span className="word word-3">science</span>
            </div>
            <div className="text-line line-2">
              <span className="word word-4">from</span>
              <span className="word word-5">your</span>
              <span className="word word-6">study</span>
              <span className="word word-7">team's</span>
            </div>
            <div className="text-line line-3">
              <span className="word word-8">research</span>
            </div>
          </div>

          {/* Animated underline accent */}
          <div className={`accent-line ${showText ? 'visible' : ''}`}></div>

          {/* Learn More Buttons */}
          <div className={`learn-more-buttons ${showText ? 'visible' : ''}`}>
            <button 
              className={`learn-more-button ${isButton1Pressed ? 'pressed' : ''}`} 
              onClick={handleButton1Click}
            >
              <img 
                src="/snorkle_vis_thumbnails/natureneurologo.jpg" 
                alt="Nature Neuroscience" 
                className="learn-more-button-logo"
              />
              <span className="learn-more-button-text">How this study's drug can rescue function in neurons</span>
              <span className="learn-more-button-arrow">→</span>
            </button>
            <button className="learn-more-button" onClick={handleButton2Click}>
              <img 
                src="/snorkle_vis_thumbnails/JN_logo.png" 
                alt="Journal of Neuroscience" 
                className="learn-more-button-logo"
              />
              <span className="learn-more-button-text">The truth about vitamin supplements for dementia</span>
              <span className="learn-more-button-arrow">→</span>
            </button>
          </div>
        </>
      )}

      {/* Video Response */}
      {showVideo && (
        <div className="animated-text-video-container">
          <video
            ref={videoRef}
            src="/snorkle_vis_thumbnails/scrolly_vid.mp4"
            autoPlay
            loop
            playsInline
            className="animated-text-video"
          />
        </div>
      )}
    </div>
  );
};

export default AnimatedTextDisplay;
